import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { xano, type Company } from "@/lib/xano";
import { useAuth } from "@/lib/auth";

const ACTIVE_KEY = "goecom_active_company";

const PREVIEW_KEY = "goecom_preview_token";

type CompanyContextValue = {
  companies: Company[];
  active: Company | null;
  activeId: number | null;
  setActiveId: (id: number) => void;
  isLoading: boolean;
  // Engine token for the active company (from the /app/company-token bridge,
  // or a magic-link preview token when not signed in).
  activeToken: string | null;
  tokenLoading: boolean;
  // Preview mode = a no-signup magic-link dashboard (token in the URL).
  previewMode: boolean;
  exitPreview: () => void;
};

const CompanyContext = createContext<CompanyContextValue | null>(null);

// A magic-link `?token=` (when not signed in) opens a read-only preview. Capture
// it once on load and remember it for the session.
function readPreviewToken(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get("token");
  if (fromUrl) {
    sessionStorage.setItem(PREVIEW_KEY, fromUrl);
    return fromUrl;
  }
  return sessionStorage.getItem(PREVIEW_KEY);
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const [previewToken, setPreviewToken] = useState<string | null>(() => readPreviewToken());
  const previewMode = status !== "authed" && !!previewToken;

  const [activeId, setActiveIdState] = useState<number | null>(() => {
    const raw = localStorage.getItem(ACTIVE_KEY);
    return raw ? Number(raw) : null;
  });

  const { data: companies = [], isLoading } = useQuery({
    queryKey: ["companies"],
    queryFn: () => xano.companies.list(),
    enabled: status === "authed",
  });

  // Default the active company to the first one once loaded.
  useEffect(() => {
    if (companies.length === 0) return;
    const stillValid = activeId && companies.some((c) => c.id === activeId);
    if (!stillValid) {
      setActiveIdState(companies[0].id);
      localStorage.setItem(ACTIVE_KEY, String(companies[0].id));
    }
  }, [companies, activeId]);

  const setActiveId = (id: number) => {
    setActiveIdState(id);
    localStorage.setItem(ACTIVE_KEY, String(id));
  };

  const active = useMemo(
    () => companies.find((c) => c.id === activeId) ?? null,
    [companies, activeId]
  );

  // Mint/reuse the engine token for the active company. Stable per company, so
  // cache it indefinitely for the session.
  const { data: tokenData, isLoading: tokenLoading } = useQuery({
    queryKey: ["company-token", active?.id],
    queryFn: () => xano.app.companyToken(active!.id),
    enabled: status === "authed" && !!active,
    staleTime: Infinity,
  });

  const exitPreview = () => {
    sessionStorage.removeItem(PREVIEW_KEY);
    setPreviewToken(null);
  };

  const value: CompanyContextValue = {
    companies,
    active,
    activeId: previewMode ? null : active?.id ?? null,
    setActiveId,
    isLoading,
    activeToken: previewMode ? previewToken : tokenData?.token ?? null,
    tokenLoading: previewMode ? false : !!active && tokenLoading,
    previewMode,
    exitPreview,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used inside <CompanyProvider>");
  return ctx;
}
